<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { useCookies } from 'vue3-cookies'
import {
  GetCurrentCartFromLocalStorage,
  GetCurrentCartIdentifiers,
  EventAddToCart
} from '../services/CartService'
import {
  GetOrderTypeId,
  GetBeeNumberAuthenicatedUser,
  GetBeeNumberSite
} from '../stores/SettingsStore'
import { CartCreate, CartGet } from '../api/Cart'
import type { IAoeCart } from '../types/CartTypes'

const logger = useLogger()
const { cookies } = useCookies()

const cart = ref<IAoeCart | null>(null)
const isProcessing = ref(false)
const message = ref('')

onMounted(async () => {
  logger.debug('AoeLinkEvent: onMounted')
  
  // Get event parameter from querystring
  const urlParams = new URLSearchParams(window.location.search)
  const eventIdParam = urlParams.get('eventId') || urlParams.get('eventid') || urlParams.get('event_id') || urlParams.get('event')
  
  let eventIdStr: string | undefined
  if (Array.isArray(eventIdParam)) {
    eventIdStr = eventIdParam.find((v): v is string => typeof v === 'string' && v.trim() !== '')
  } else if (typeof eventIdParam === 'string' && eventIdParam.trim() !== '') {
    eventIdStr = eventIdParam
  }

  if (!eventIdStr) {
    logger.warn('AoeLinkEvent: No event parameter found in querystring')
    message.value = 'No event specified'
    return
  }

  const eventId = parseInt(eventIdStr)
  if (isNaN(eventId)) {
    logger.warn('AoeLinkEvent: Invalid event ID:', eventIdStr)
    message.value = 'Invalid event ID'
    return
  }

  logger.debug('AoeLinkEvent: Processing event ID:', eventId)
  isProcessing.value = true
  message.value = 'Linking cart to event...'

  try {
    // Check if cart exists
    let cartIdToken = GetCurrentCartIdentifiers()?.aoeCartIdToken || ''
    
    if (!cartIdToken) {
      // No cart exists, create one
      logger.debug('AoeLinkEvent: No cart found, creating new cart')
      message.value = 'Creating cart...'
      
      const orderTypeIdVal = await GetOrderTypeId()
      const customerBeeNumber = await GetBeeNumberAuthenicatedUser()
      const beeNumberPayee = await GetBeeNumberSite()
      
      const cartCreateResp = await CartCreate({
        orderTypeId: orderTypeIdVal,
        beeNumberPayee: beeNumberPayee,
        beeNumberCustomer: customerBeeNumber,
      })

      if (cartCreateResp && cartCreateResp.aoeCartIdToken) {
        const cartGetResp = await CartGet(cartCreateResp.aoeCartIdToken)
        
        if (cartGetResp && cartGetResp.item) {
          cartIdToken = cartGetResp.item.aoeCartIdToken
          cart.value = cartGetResp.item
          
          // Save cart token to cookie
          cookies.set('AoeCartIdToken', cartIdToken)
          logger.debug('AoeLinkEvent: Cart created with token:', cartIdToken)
        }
      }
      
      if (!cartIdToken) {
        // Fallback: try local storage
        cart.value = await GetCurrentCartFromLocalStorage()
        cartIdToken = cart.value?.aoeCartIdToken || ''
      }
    } else {
      // Cart exists, load it
      logger.debug('AoeLinkEvent: Cart found with token:', cartIdToken)
      cart.value = await GetCurrentCartFromLocalStorage()
    }

    if (!cartIdToken) {
      logger.error('AoeLinkEvent: Failed to get or create cart')
      message.value = 'Failed to create cart'
      isProcessing.value = false
      return
    }

    // Check if cart is already linked to this event
    if (cart.value && cart.value.eventId === eventId) {
      logger.debug('AoeLinkEvent: Cart already linked to event:', eventId)
      message.value = 'Cart already linked to event'
      isProcessing.value = false
      return
    }

    // Link cart to event
    logger.debug('AoeLinkEvent: Linking cart to event:', eventId)
    message.value = 'Linking to event...'
    
    await EventAddToCart({ aoeCartIdToken: cartIdToken, eventId })
    
    // Refresh cart from local storage
    cart.value = await GetCurrentCartFromLocalStorage()
    
    logger.debug('AoeLinkEvent: Successfully linked cart to event:', eventId)
    message.value = 'Successfully linked to event!'
    
  } catch (error) {
    logger.error('AoeLinkEvent: Error linking cart to event:', error)
    message.value = 'Error linking cart to event'
  } finally {
    isProcessing.value = false
  }
})
</script>

<template>
  <div class="aoe-link-event">
  </div>
</template>

<style lang="scss" scoped>
.aoe-link-event {
  padding: $card-padding;
  text-align: center;
}

.processing {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2-5;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(black, 0.1);
  border-radius: 50%;
  border-top-color: var(--vt-c-teal-dark);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.message {
  @include typography-body;
  color: #333;
}
</style>
